import { API_HOST } from "@/const";
import { getLoginToken } from "@/models/userInfo";
import { v4 as uuidv4 } from "uuid";

const STORAGE_KEY = "lb-chat-abnormal-report-queue-v2";
const USER_INFO_STORAGE_KEY = "lbChatUserInfo";
const MAX_QUEUE_SIZE = 100;
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const BASE_RETRY_DELAY = 5000;
const MAX_RETRY_DELAY = 30 * 60 * 1000;
const REPORT_URL = `${API_HOST}/chat-server/api/chat/abnormal-event/report`;
const CANCEL_URL = `${API_HOST}/chat-server/api/chat/request/cancel`;

export type ChatAbnormalType =
  | "NETWORK_ERROR"
  | "SSE_DISCONNECT"
  | "FIRST_TOKEN_TIMEOUT"
  | "STREAM_TIMEOUT"
  | "HTTP_ERROR"
  | "EMPTY_RESPONSE"
  | "PARTIAL_RESPONSE"
  | "CLIENT_PARSE_ERROR"
  | "SSE_ERROR";

export interface ChatAbnormalReport {
  eventId: string;
  requestId: string;
  sessionId: string;
  messageId?: string;
  model?: string;
  errorType: ChatAbnormalType;
  errorCode?: string;
  errorMessage?: string;
  occurTime: string;
  online: boolean;
  pageUrl: string;
  ownerKey: string;
  queuedAt: number;
  retryCount: number;
  nextRetryAt: number;
}

let flushing = false;
let initialized = false;
let retryTimer: number | null = null;

export const createChatRequestId = () => uuidv4();

export const formatLocalDateTime = (date = new Date()) => {
  const pad = (value: number) => value.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )}`;
};

export const reportChatAbnormal = (
  report: Omit<
    ChatAbnormalReport,
    | "eventId"
    | "occurTime"
    | "online"
    | "pageUrl"
    | "ownerKey"
    | "queuedAt"
    | "retryCount"
    | "nextRetryAt"
  >,
) => {
  const ownerKey = getCurrentOwnerKey();
  if (!ownerKey) return;
  const now = Date.now();
  const item: ChatAbnormalReport = {
    ...report,
    eventId: uuidv4(),
    occurTime: formatLocalDateTime(),
    online: typeof navigator === "undefined" ? true : navigator.onLine,
    pageUrl:
      typeof location === "undefined"
        ? ""
        : `${location.pathname}${location.search}`.slice(0, 500),
    errorMessage: report.errorMessage?.slice(0, 500),
    ownerKey,
    queuedAt: now,
    retryCount: 0,
    nextRetryAt: now,
  };
  writeQueue([...readQueue(), item]);
  void flushChatAbnormalReports();
};

export const flushChatAbnormalReports = async () => {
  const ownerKey = getCurrentOwnerKey();
  const token = getLoginToken();
  if (
    flushing ||
    !ownerKey ||
    typeof window === "undefined" ||
    !navigator.onLine ||
    !token
  ) {
    return;
  }
  flushing = true;
  const completedEventIds = new Set<string>();
  const retryUpdates = new Map<string, Pick<ChatAbnormalReport, "retryCount" | "nextRetryAt">>();
  try {
    const queue = readQueue();
    for (const item of queue) {
      if (item.ownerKey !== ownerKey || item.nextRetryAt > Date.now()) continue;
      try {
        const response = await fetch(REPORT_URL, {
          method: "POST",
          headers: {
            token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(stripLocalFields(item)),
          keepalive: true,
        });
        const result = await response.json().catch(() => null);
        if (response.ok && result?.code === 0) {
          completedEventIds.add(item.eventId);
          continue;
        }
        // The monitoring endpoint is best-effort. A 2xx application-level
        // rejection (for example, the chat request never became durable) is
        // terminal and must not remain in local storage for repeated reporting.
        if (response.ok && result?.code !== undefined) {
          completedEventIds.add(item.eventId);
          continue;
        }
        if ([400, 404, 422].includes(response.status)) {
          completedEventIds.add(item.eventId);
          continue;
        }
        // 429、5xx、登录态异常以及未知业务码都保留，等待当前用户恢复后重试。
        retryUpdates.set(item.eventId, nextRetry(item));
      } catch (_error) {
        retryUpdates.set(item.eventId, nextRetry(item));
      }
    }
  } finally {
    const latestQueue = readQueue()
      .filter((item) => !completedEventIds.has(item.eventId))
      .map((item) => ({ ...item, ...retryUpdates.get(item.eventId) }));
    writeQueue(latestQueue);
    flushing = false;
    scheduleNextFlush(latestQueue, ownerKey);
  }
};

export const notifyChatRequestCancelled = async (
  requestId?: string,
  sessionId?: string,
  reason = "USER_REQUESTED",
  shouldContinue: () => boolean = () => true,
) => {
  const token = getLoginToken();
  if (!requestId || !sessionId || !token) return null;
  const deadline = Date.now() + 12000;
  // 仅重试服务端明确返回的 NOT_READY，不重试鉴权失败、业务拒绝或未知网络错误。
  for (let attempt = 0; attempt < 8; attempt++) {
    if (!shouldContinue() || getLoginToken() !== token || Date.now() >= deadline) return null;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), Math.min(5000, deadline - Date.now()));
    try {
      const response = await fetch(CANCEL_URL, {
        method: "POST",
        headers: {
          token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ requestId, sessionId, reason }),
        keepalive: true,
        signal: controller.signal,
      });
      if (!response.ok) return null;
      const result = await response.json();
      if (result?.code !== 0) return null;
      const accepted = result.data === true || result.data?.cancelAccepted === true || result.data?.status === "CANCELLED";
      const retryable = !accepted && result.data?.retryable === true && result.data?.status === "NOT_READY";
      if (!retryable) return { accepted, status: result.data?.status as string | undefined };
    } catch (_error) {
      return null;
    } finally {
      window.clearTimeout(timeoutId);
    }
    if (attempt < 7 && shouldContinue() && getLoginToken() === token) {
      await new Promise(resolve => window.setTimeout(resolve, Math.min(200 * (attempt + 1), 1000)));
    }
  }
  return null;
};

export const initChatAbnormalReporter = () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  window.addEventListener("online", flushChatAbnormalReports);
  window.addEventListener("storage", (event) => {
    if (event.key === USER_INFO_STORAGE_KEY) void flushChatAbnormalReports();
  });
  void flushChatAbnormalReports();
};

const stripLocalFields = ({
  queuedAt: _queuedAt,
  ownerKey: _ownerKey,
  retryCount: _retryCount,
  nextRetryAt: _nextRetryAt,
  ...report
}: ChatAbnormalReport) => report;

const getCurrentOwnerKey = (): string | null => {
  if (typeof localStorage === "undefined") return null;
  try {
    const userInfo = JSON.parse(localStorage.getItem(USER_INFO_STORAGE_KEY) || "null");
    const siteId = userInfo?.loginSiteId || userInfo?.regSiteId;
    return userInfo?.id && siteId ? `${userInfo.id}:${siteId}` : null;
  } catch (_error) {
    return null;
  }
};

const nextRetry = (item: ChatAbnormalReport) => {
  const retryCount = item.retryCount + 1;
  const exponentialDelay = Math.min(
    MAX_RETRY_DELAY,
    BASE_RETRY_DELAY * 2 ** Math.min(retryCount - 1, 10),
  );
  return {
    retryCount,
    nextRetryAt: Date.now() + exponentialDelay + Math.floor(Math.random() * 1000),
  };
};

const scheduleNextFlush = (queue: ChatAbnormalReport[], ownerKey: string) => {
  if (typeof window === "undefined") return;
  if (retryTimer !== null) window.clearTimeout(retryTimer);
  const nextRetryAt = queue
    .filter((item) => item.ownerKey === ownerKey)
    .reduce((minimum, item) => Math.min(minimum, item.nextRetryAt), Infinity);
  if (!Number.isFinite(nextRetryAt)) {
    retryTimer = null;
    return;
  }
  retryTimer = window.setTimeout(
    () => void flushChatAbnormalReports(),
    Math.max(1000, nextRetryAt - Date.now()),
  );
};

const readQueue = (): ChatAbnormalReport[] => {
  if (typeof localStorage === "undefined") return [];
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(value)) return [];
    const minimumTime = Date.now() - MAX_AGE;
    return value
      .filter(
        (item) =>
          item &&
          typeof item.requestId === "string" &&
          typeof item.eventId === "string" &&
          typeof item.ownerKey === "string" &&
          Number(item.queuedAt) >= minimumTime,
      )
      .map((item) => ({
        ...item,
        retryCount: Number(item.retryCount) || 0,
        nextRetryAt: Number(item.nextRetryAt) || Date.now(),
      }))
      .slice(-MAX_QUEUE_SIZE);
  } catch (_error) {
    return [];
  }
};

const writeQueue = (queue: ChatAbnormalReport[]) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE_SIZE)));
  } catch (_error) {
    // 监控补报不能影响聊天主流程（例如无痕模式或存储空间不足）。
  }
};
